import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { fetchBaseCurrencies } from '@/features/player-reports/lib/api';
import { BetInspection } from '@/features/bet-inspection/BetInspection';
import { CompareBets } from '@/features/compare/CompareBets';
import { DailyReports } from '@/features/daily-reports/DailyReports';
import { GameReports } from '@/features/game-reports/GameReports';
import { PlayerInspection } from '@/features/player-inspection/PlayerInspection';
import { DelayDelivery } from '@/features/delay-delivery/DelayDelivery';
import { ExchangeRates } from '@/features/exchange-rates/ExchangeRates';
import { Reconciliation } from '@/features/reconciliation/Reconciliation';
import { RuleBuilder } from '@/features/rule-builder/RuleBuilder';
import { PlayerReports } from '@/features/player-reports/PlayerReports';
import { Transactions } from '@/features/transactions/Transactions';
import {
  readBootstrap,
  readViewFromHash,
  setThemeInHash,
  setViewInHash,
  type LookupParams,
} from '@/lib/bootstrap';
import type { TimeZoneMode } from '@/lib/format';
import { BootstrapForm } from './Bootstrap';
import { Navbar } from './Navbar';
import { DEFAULT_VIEW, isViewId, type ViewId } from './view';

/**
 * One view's slot.
 *
 * ⚠️ Declared at module scope, NOT inside App. A component defined during
 * render is a NEW type on every render, so React would unmount and remount its
 * subtree each time App re-rendered — destroying exactly the state this whole
 * mechanism exists to preserve.
 *
 * Inactive views are hidden with the `hidden` attribute rather than unmounted:
 * that also removes the subtree from the accessibility tree and the tab order,
 * so a background view cannot be reached by keyboard.
 */
function Slot({
  id,
  view,
  visited,
  children,
}: {
  id: ViewId;
  view: ViewId;
  visited: Set<ViewId>;
  children: ReactNode;
}) {
  if (!visited.has(id)) return null;
  return <div hidden={id !== view}>{children}</div>;
}

/**
 * The app shell.
 *
 * betlens is a multi-tool now: the shell owns what's global — bootstrap context,
 * theme, timezone, and which functional area (view) is active — and hands each
 * feature only what it needs. Adding a feature is a `VIEWS` entry plus its
 * component; the shell wires the rest.
 */
export default function App() {
  const [boot, setBoot] = useState(() => readBootstrap());
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark',
  );
  const [tz, setTz] = useState<TimeZoneMode>('utc');
  // Global display setting, like theme and timezone: which currency converted
  // figures are expressed in. Reports filtered to a single currency ignore it.
  const [base, setBase] = useState('USD');

  // Which currencies we hold an FX rate for. Fetched once; the picker falls back
  // to the current base if the call fails, so the control is never empty.
  const baseCurrencies = useQuery({
    queryKey: ['base-currencies', boot.api],
    queryFn: ({ signal }) => fetchBaseCurrencies({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 60 * 60_000,
    retry: 1,
  });
  const [view, setView] = useState<ViewId>(() => {
    const fromHash = readViewFromHash();
    return isViewId(fromHash) ? fromHash : DEFAULT_VIEW;
  });
  // A lookup handed to Bet Inspection when another view links into it (e.g. a
  // Transactions row's "Inspect this bet"). Bumped each time so the same bet can
  // be re-opened.
  const [focus, setFocus] = useState<{ lookup: LookupParams; nonce: number } | null>(null);

  /**
   * Views a user has opened. A view is mounted on first visit and then KEPT
   * mounted (hidden when inactive), which is what makes switching tools
   * non-destructive: filters, results, expanded rows and sort order all survive
   * because nothing unmounts. Unvisited views cost nothing.
   */
  const [visited, setVisited] = useState<Set<ViewId>>(() => new Set([view]));

  /** Scroll offset per view — restored on return, like a browser tab. */
  const scrollByView = useRef<Partial<Record<ViewId, number>>>({});

  const goToView = (next: ViewId) => {
    if (next === view) return;
    scrollByView.current[view] = window.scrollY;
    setVisited((seen) => (seen.has(next) ? seen : new Set(seen).add(next)));
    setView(next);
    setViewInHash(next);
  };

  // Before paint, so the restored position never flashes at the top first.
  useLayoutEffect(() => {
    window.scrollTo(0, scrollByView.current[view] ?? 0);
  }, [view]);

  const openBet = (lookup: LookupParams) => {
    setFocus((f) => ({ lookup, nonce: (f?.nonce ?? 0) + 1 }));
    goToView('inspection');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem('betlens.theme', next);
    } catch {
      /* private mode */
    }
    setThemeInHash(next);
  };

  // Not embedded (no api in the hash): show the dev bootstrap form first.
  if (!boot.api) {
    return (
      <BootstrapForm
        initialApi={boot.api}
        apiError={boot.apiError}
        onReady={(v) => setBoot((b) => ({ ...b, api: v.api, token: v.token || null }))}
      />
    );
  }

  return (
    <Tooltip.Provider delayDuration={200} skipDelayDuration={300}>
      <div className="noise flex min-h-full flex-col">
        <Navbar
          view={view}
          onView={goToView}
          theme={theme}
          onToggleTheme={toggleTheme}
          tz={tz}
          onToggleTz={() => setTz((t) => (t === 'utc' ? 'local' : 'utc'))}
          base={base}
          onBase={setBase}
          baseCurrencies={baseCurrencies.data ?? []}
        />

        <Slot id="inspection" view={view} visited={visited}>
          <BetInspection boot={boot} tz={tz} focus={focus} />
        </Slot>
        <Slot id="transactions" view={view} visited={visited}>
          <Transactions boot={boot} tz={tz} onOpenBet={openBet} />
        </Slot>
        <Slot id="delivery" view={view} visited={visited}>
          <DelayDelivery boot={boot} tz={tz} onOpenBet={openBet} />
        </Slot>
        <Slot id="reports" view={view} visited={visited}>
          <PlayerReports boot={boot} tz={tz} base={base} />
        </Slot>
        <Slot id="games" view={view} visited={visited}>
          <GameReports boot={boot} tz={tz} base={base} onOpenBet={openBet} />
        </Slot>
        <Slot id="player" view={view} visited={visited}>
          <PlayerInspection boot={boot} tz={tz} base={base} />
        </Slot>
        <Slot id="finance" view={view} visited={visited}>
          <DailyReports boot={boot} tz={tz} base={base} />
        </Slot>
        <Slot id="reconciliation" view={view} visited={visited}>
          <Reconciliation boot={boot} tz={tz} onOpenBet={openBet} />
        </Slot>
        <Slot id="rates" view={view} visited={visited}>
          <ExchangeRates boot={boot} tz={tz} base={base} />
        </Slot>
        <Slot id="compare" view={view} visited={visited}>
          <CompareBets boot={boot} />
        </Slot>
        <Slot id="rules" view={view} visited={visited}>
          <RuleBuilder boot={boot} />
        </Slot>
      </div>
    </Tooltip.Provider>
  );
}
